import { z } from "zod";

// Job workflows: what the tech presses on each kind of job, in order, and
// what each press asks for. Statuses stay the same four (scheduled, in
// progress, done, canceled); the workflow decides which steps show, what
// "Partial complete" and "Dry run" do, and what the office hears about.
//
//   Service   On my way → I'm here (photos + notes) → Finish / Partial / Dry run
//   Install   I'm here → Finish / Partial / Dry run          (no "on my way")
//   Warranty  On my way → I'm here → Finish / Partial / Dry run (photo for the rep)

export const DryRunOutcomeSchema = z.enum(["off", "reschedule", "office_reschedule", "photo_rep"]);
export type DryRunOutcome = z.infer<typeof DryRunOutcomeSchema>;

export const JobWorkflowSchema = z.object({
  /** "On my way" before arriving (texts the customer). Installs: off. */
  onTheWay: z.boolean().default(true),
  /** Arriving asks for photos of what was found, and a note. */
  arrivePhotos: z.boolean().default(true),
  arriveNotes: z.boolean().default(true),
  /** "Partial complete": done for today, a return visit is opened for dispatch. */
  partial: z.boolean().default(true),
  /** "Dry run": the crew couldn't do the work. What happens next. */
  dryRun: DryRunOutcomeSchema.default("reschedule"),
  /** Finishing offers the invoice. */
  completeInvoice: z.boolean().default(true),
  /** Ask the customer to OK the visit first (customer portal). Shown on the job; never a gate. */
  approveToStart: z.boolean().default(false),
});
export type JobWorkflow = z.infer<typeof JobWorkflowSchema>;

export const DRY_RUN_LABEL: Record<DryRunOutcome, string> = {
  off: "Not offered",
  reschedule: "Needs a new date (back to dispatch)",
  office_reschedule: "Office contacts the customer to reschedule",
  photo_rep: "Tech takes a photo for the rep, then a new date",
};

/** What a job type starts with when the company hasn't set its workflow. */
export function defaultWorkflowFor(jobType: string): JobWorkflow {
  const t = jobType.toLowerCase();
  if (/install|production|build/.test(t)) return { onTheWay: false, arrivePhotos: true, arriveNotes: true, partial: true, dryRun: "reschedule", completeInvoice: false, approveToStart: false };
  if (/warranty/.test(t)) return { onTheWay: true, arrivePhotos: true, arriveNotes: true, partial: true, dryRun: "photo_rep", completeInvoice: false, approveToStart: false };
  if (/estimate|quote|job walk|walk/.test(t)) return { onTheWay: true, arrivePhotos: true, arriveNotes: true, partial: false, dryRun: "office_reschedule", completeInvoice: false, approveToStart: false };
  return { onTheWay: true, arrivePhotos: true, arriveNotes: true, partial: true, dryRun: "office_reschedule", completeInvoice: true, approveToStart: false };
}

/** The workflow for a job: the company's setting for its type, else the default for that type. */
export function workflowFor(jobType: string | undefined, settings: Record<string, Partial<JobWorkflow>> | undefined): JobWorkflow {
  const base = defaultWorkflowFor(jobType ?? "");
  const set = jobType ? settings?.[jobType] : undefined;
  return set ? { ...base, ...JobWorkflowSchema.partial().parse(set) } : base;
}

/** One dry run on a job: the crew showed up and couldn't do the work. */
export const DryRunSchema = z.object({
  at: z.number(),
  byUid: z.string(),
  byName: z.string().default(""),
  reason: z.string().max(1000),
  /** jobs/{id}/photos/{photoId}: the proof-of-visit photo, when the workflow asks for one. */
  photoId: z.string().nullable().default(null),
  outcome: DryRunOutcomeSchema,
});
export type DryRun = z.infer<typeof DryRunSchema>;
